import { useEffect, useRef, useState } from 'react';
import { useAppSelector, useAppDispatch } from 'flow/hooks/useRedux';
import { generatePath, Link } from 'react-router-dom';
import { fetchChatSessions, unArchiveSession } from 'flow/actions/chat-actions';
import BaseModal from 'flow/components/ui-components/BaseModal';
import Button from 'flow/components/ui-components/Button';
import ChatAvatar from 'flow/components/ui-components/ChatAvatar';
import SearchInput from 'flow/components/ui-components/SearchInput';
import Tooltip from 'flow/components/ui-components/Tooltip';
import { useModal } from 'flow/contexts/ModalProvider';
import useAppContext from 'flow/hooks/useAppContext';
import { selectSortedArchivedChatSessions } from 'flow/selectors/chat-selectors';
import { ChatSession } from 'flow/types/Chats';
import { ChatSessionKey } from 'flow/types/EntityKeys';
import { routes } from 'flow/types/routes';

import { TypedMap } from 'flow/types/TypedMap';
import { getFormattedTime } from 'flow/utils';
import { twMerge } from 'tailwind-merge';
import { isMobileDevice } from 'flow/utils/app-utils';

const ArchivedChatsModal = () => {
    const dispatch = useAppDispatch();
    const { i18next } = useAppContext();
    const { closeModal } = useModal();
    const timeoutId = useRef<ReturnType<typeof setTimeout>>(null);
    const [searchTerm, setSearchTerm] = useState('');

    const archivedChatSessions: TypedMap<
        Record<ChatSessionKey, TypedMap<ChatSession>>
    > = useAppSelector(selectSortedArchivedChatSessions);

    useEffect(() => {
        dispatch(fetchChatSessions('contact'));
    }, []);

    const handleInput = (text: string) => {
        clearTimeout(timeoutId.current);

        timeoutId.current = setTimeout(() => {
            setSearchTerm(text);
        }, 400);
    };

    return (
        <BaseModal
            open={true}
            header={i18next.t('chat.archived.title')}
            className="h-[39.5rem] w-96"
        >
            <div className="flex flex-col gap-y-3">
                <div className="flex items-center gap-x-3">
                    <SearchInput
                        iconKey="search"
                        className="flex-1"
                        autoFocus={true}
                        onChange={(e) => handleInput(e.target?.value)}
                    />
                </div>

                <div>
                    {archivedChatSessions
                        .valueSeq()
                        .filter((session: TypedMap<ChatSession>) =>
                            session
                                .get('name')
                                ?.toLowerCase()
                                .includes(searchTerm.toLowerCase()),
                        )
                        .map((session: TypedMap<ChatSession>) => {
                            const latestMessage = session.get('latestMessage');

                            return (
                                <Link
                                    key={session.get('key')}
                                    to={generatePath(routes['messages.id'], {
                                        id: session.get('key'),
                                    })}
                                    onClick={closeModal}
                                >
                                    <div className="group flex h-13 items-center gap-x-3 rounded-sm p-2 hover:bg-gray-200">
                                        <ChatAvatar session={session} />

                                        <div className="flex flex-1 flex-col truncate">
                                            <p className="truncate text-base/5">
                                                {session.get('name')}
                                            </p>
                                            {latestMessage && (
                                                <p
                                                    dir="auto"
                                                    className="truncate text-sm text-gray-500"
                                                >
                                                    {latestMessage.get(
                                                        'message',
                                                    )}
                                                </p>
                                            )}
                                        </div>

                                        {latestMessage && (
                                            <p
                                                className={twMerge(
                                                    'text-sm/4 text-gray-500',
                                                    isMobileDevice()
                                                        ? 'hidden'
                                                        : 'group-focus-visible-within:hidden group-hover:hidden',
                                                )}
                                            >
                                                {getFormattedTime(
                                                    latestMessage.get('sent'),
                                                    i18next,
                                                )}
                                            </p>
                                        )}

                                        <div
                                            className={
                                                isMobileDevice()
                                                    ? 'block'
                                                    : 'sr-only-until-hover-or-focus block'
                                            }
                                        >
                                            <Tooltip
                                                label={i18next.t(
                                                    'chatsetting.unarchive',
                                                )}
                                            >
                                                <Button
                                                    iconKey="unarchive"
                                                    onClick={(e) => {
                                                        e.preventDefault();
                                                        e.stopPropagation();

                                                        dispatch(
                                                            unArchiveSession(
                                                                session.get(
                                                                    'key',
                                                                ),
                                                            ),
                                                        );
                                                    }}
                                                />
                                            </Tooltip>
                                        </div>
                                    </div>
                                </Link>
                            );
                        })}
                </div>
            </div>
        </BaseModal>
    );
};

export default ArchivedChatsModal;
